import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { Chip } from '../../components/ui/chip'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'
import { errorCode, errorReason, sessionGone } from '../../lib/errors'
import { useI18n } from '../lib/i18n'
import {
  companyGone,
  companyKey,
  hrTeamKey,
  organizationChanged,
  useHrTeam,
  type CompanyDetail,
  type HrPerson,
  type HrTeam,
  type Reopen,
} from '../lib/screens'
import { forCompany, useTRPCClient } from '../lib/trpc'
import { ErrorState, Loading } from './chrome'

/*
 * The HR team of a company (ADR 0009 §8; PRD 04 §2.2): the name, the email and the state of each
 * person with the HR role, invited or active, never anyone else of its staff: the only names and
 * emails the panel shows, once the company's detail has opened it, and each view an entry in the
 * company's trail. For someone still invited, their last invitation and «Reenviar invitación»
 * («Enviar invitación» if none was sent), only while the company is active (a suspended company's
 * invitations cannot be accepted) and once it has its privacy notice; a confirmation first (decided
 * on 2026-11-02: each one is an email), and no passkey, as HR's console. Not in the Figma: marked
 * for design review.
 */
export function HrTeamSection({
  company,
  reopen,
}: {
  company: CompanyDetail
  /** Another tab opened another company: the way to open this one again. */
  reopen: Reopen
}) {
  const { t } = useI18n()
  const id = useId()
  const team = useHrTeam(company.id, true)
  // The team was refused because another company was open: once this one is open again, the
  // team is asked for again (a view, and its entry in the company's trail).
  const reopenTeam = async () => {
    if ((await reopen()).isSuccess) await team.refetch()
  }
  return (
    <section aria-labelledby={id} className="space-y-4">
      <div>
        <h2 id={id} className="text-xl">
          {t('company.hr.title')}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-muted">{t('company.hr.body')}</p>
      </div>
      {team.isPending ? (
        <Loading />
      ) : team.isError ? (
        organizationChanged(team.error) ? (
          <div>
            <p role="alert" className="text-sm text-danger">
              {t('common.error.organizationChanged')}
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void reopenTeam()}
            >
              {t('company.reopen')}
            </Button>
          </div>
        ) : (
          <ErrorState onRetry={() => void team.refetch()} />
        )
      ) : (
        <TeamList company={company} team={team.data} reopen={reopen} />
      )}
    </section>
  )
}

/** What the page says once an invitation is sent, or why it could not be, by person. */
interface Outcome {
  userId: string
  tone: 'done' | 'error'
  text: string
  reopen?: boolean
}

function TeamList({
  company,
  team,
  reopen,
}: {
  company: CompanyDetail
  team: HrTeam
  reopen: Reopen
}) {
  const { t } = useI18n()
  const client = useTRPCClient()
  const queryClient = useQueryClient()
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  // The person the confirmation asks about, which stays while it closes, and the button that opened
  // it, where the focus goes back. One dialog for the whole list.
  const [asked, setAsked] = useState<HrPerson | null>(null)
  const [confirming, setConfirming] = useState(false)
  const opener = useRef<HTMLButtonElement | null>(null)

  const showInvitation = (userId: string, invitation: HrPerson['invitation']) =>
    queryClient.setQueryData<HrTeam>(
      hrTeamKey(company.id),
      (d) =>
        d && {
          ...d,
          people: d.people.map((p) => (p.id === userId ? { ...p, invitation } : p)),
        },
    )

  /** Why no invitation may go now, if one may not: the company suspended, or without its notice. */
  const blocked =
    company.status !== 'active'
      ? t('company.hr.suspended')
      : !team.notice
        ? t('company.hr.noNotice')
        : null

  const resend = useMutation({
    mutationFn: (userId: string) =>
      client.companies.resendInvitation.mutate({ userId }, forCompany(company.id)),
    onSuccess: (r) => {
      showInvitation(r.userId, r.invitation)
      setOutcome({ userId: r.userId, tone: 'done', text: t('company.hr.resent') })
    },
    onError: (err, userId) => {
      if (sessionGone(err)) return
      const reason = errorReason(err)
      // Suspended meanwhile (another operator, another tab): the page shows it as it is.
      if (reason === 'suspended')
        queryClient.setQueryData<CompanyDetail>(
          companyKey(company.id),
          (d) => d && { ...d, status: 'suspended' },
        )
      const text =
        reason === 'invitation-limit'
          ? t('company.hr.error.limit')
          : reason === 'not-invitable'
            ? t('company.hr.error.notInvitable')
            : reason === 'no-notice'
              ? t('company.hr.noNotice')
              : reason === 'suspended'
                ? t('company.hr.suspended')
                : organizationChanged(err)
                  ? t('common.error.organizationChanged')
                  : companyGone(err)
                    ? t('company.hr.error.notFound')
                    : errorCode(err) === 'TOO_MANY_REQUESTS'
                      ? t('common.error.rateLimited')
                      : t('common.error.generic')
      setOutcome({ userId, tone: 'error', text, reopen: organizationChanged(err) })
    },
  })

  if (team.people.length === 0)
    return (
      <p className="rounded-card border border-line bg-surface px-5 py-4 text-sm text-ink-muted">
        {t('company.hr.empty')}
      </p>
    )
  return (
    <div className="space-y-3">
      {blocked && team.people.some((p) => p.status === 'invited') && (
        <p className="rounded-lg bg-page px-4 py-3 text-sm">{blocked}</p>
      )}
      <ul className="divide-y divide-line rounded-card border border-line bg-surface">
        {team.people.map((person) => (
          <PersonRow
            key={person.id}
            person={person}
            invitable={!blocked}
            busy={resend.isPending && resend.variables === person.id}
            outcome={outcome?.userId === person.id ? outcome : null}
            onResend={(button) => {
              if (resend.isPending) return
              setOutcome(null)
              opener.current = button
              setAsked(person)
              setConfirming(true)
            }}
            reopen={reopen}
          />
        ))}
      </ul>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t(asked?.invitation ? 'company.hr.resend' : 'company.hr.send')}
        confirmLabel={t(asked?.invitation ? 'company.hr.resend' : 'company.hr.send')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          setConfirming(false)
          if (asked) resend.mutate(asked.id)
        }}
        opener={opener}
      >
        <p className="[overflow-wrap:anywhere]">
          <span className="font-medium">
            {asked?.firstName} {asked?.lastName}
          </span>
          <br />
          {asked?.email}
        </p>
        <p>{t(asked?.invitation ? 'company.hr.confirm.resend' : 'company.hr.confirm.send')}</p>
      </ConfirmDialog>
    </div>
  )
}

function PersonRow({
  person,
  invitable,
  busy,
  outcome,
  onResend,
  reopen,
}: {
  person: HrPerson
  /** An invitation may go: the company is active and has its notice. */
  invitable: boolean
  busy: boolean
  outcome: Outcome | null
  onResend: (button: HTMLButtonElement) => void
  reopen: Reopen
}) {
  const { t } = useI18n()
  const nameId = useId()
  const invitation = person.invitation
  return (
    <li className="flex flex-wrap items-start gap-3 px-5 py-4">
      <div className="min-w-0 flex-1">
        <p id={nameId} className="font-medium [overflow-wrap:anywhere]">
          {person.firstName} {person.lastName}
        </p>
        <p className="text-sm text-ink-muted [overflow-wrap:anywhere]">{person.email}</p>
        {person.status === 'invited' && (
          <p className="mt-1 text-xs text-ink-muted">
            {invitation
              ? t(
                  invitation.usable
                    ? 'company.hr.invitation.usable'
                    : 'company.hr.invitation.expired',
                  { at: new Date(invitation.sentAt) },
                )
              : t('company.hr.invitation.none')}
          </p>
        )}
        {/* Always in the row, so that it is read out as it changes. */}
        <p role="status" className="text-sm text-success not-empty:mt-2">
          {outcome?.tone === 'done' ? outcome.text : ''}
        </p>
        <p role="alert" className="text-sm text-danger not-empty:mt-2">
          {outcome?.tone === 'error' ? outcome.text : ''}
        </p>
        {outcome?.reopen && (
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => void reopen()}>
            {t('company.reopen')}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Chip tone={person.status === 'active' ? 'success' : 'light'}>
          {t(`company.hr.status.${person.status}`)}
        </Chip>
        {person.status === 'invited' && invitable && (
          <Button
            variant="secondary"
            size="sm"
            aria-describedby={nameId}
            aria-disabled={busy || undefined}
            className="aria-disabled:cursor-progress aria-disabled:opacity-50"
            onClick={(e) => onResend(e.currentTarget)}
          >
            {busy
              ? t('company.hr.resend.busy')
              : t(invitation ? 'company.hr.resend' : 'company.hr.send')}
          </Button>
        )}
      </div>
    </li>
  )
}
